import { useCallback, useEffect, useRef } from "react";

import {
	type AskMessage,
	AskSSEEventSchema,
	type AskSSEInterviewerOutputEvent,
	type InterviewerHarnessType,
	MAX_CURRENT_FORM_NAME_LENGTH,
	MAX_CURRENT_FORM_QUESTIONS,
	MAX_CURRENT_FORM_QUESTION_LENGTH,
	MAX_CURRENT_FORM_SUBTEXT_LENGTH,
} from "@performyard/service-contracts";

import { useEditFormStore } from "../../../pages/Administration/Reviews/EditForm/useEditFormStore";

import type { FormBuilderContextSnapshot } from "../types";

import { getInterviewerNotesKey } from "../store/helpers";

import { getHeaders, getUrl } from "../../../api/services/defs/internal-api/internal-base-request";

import { extractSSEMessages, parseSSEMessage } from "../utils";
import { isCompetencyReimportBlocked } from "../utils/is-competency-reimport-blocked";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Idle timeout in milliseconds — reset on each received event (2 minutes)
const STREAM_TIMEOUT_MS = 120_000;

type SendQueryOptions = {
	query: string;
	messages?: AskMessage[];
	agent?: "form" | "goal-coach" | "form-building-coach";
	/** Routes this turn to an interviewer harness regardless of the active one. */
	harness?: InterviewerHarnessType;
	documentContent?: string;
	omitUserMessage?: boolean;
};

type UseAskStreamReturn = {
	sendQuery: (options: SendQueryOptions) => Promise<void>;
	abort: () => void;
	isStreaming: boolean;
};

/** Hook to send queries and handle SSE streaming responses. */
export const useAskStream = (): UseAskStreamReturn => {
	const abortControllerRef = useRef<AbortController | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

	// Cleanup on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			readerRef.current?.cancel();
			readerRef.current = null;
		};
	}, []);

	// Subscribe to status for isStreaming check
	const status = useAskPerformYardStore((state) => state.status);

	// Get actions via getState() to avoid stale closures in async operations
	// This is the recommended Zustand pattern for actions used in callbacks
	const getStoreActions = useCallback(() => useAskPerformYardStore.getState(), []);

	const clearTimeoutRef = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const abort = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		clearTimeoutRef();
		readerRef.current?.cancel();
		readerRef.current = null;
		const { setStatus, setStreamStage, setStreamMessage } = getStoreActions();
		setStatus("idle");
		setStreamStage(null);
		setStreamMessage(null);
	}, [clearTimeoutRef, getStoreActions]);

	const sendQuery = useCallback(
		async ({
			query,
			messages = [],
			agent,
			harness,
			documentContent,
			omitUserMessage = false,
		}: SendQueryOptions): Promise<void> => {
			// Get fresh store state and actions to avoid stale closures
			const {
				sessionId,
				addUserMessage,
				startAssistantMessage,
				appendToAssistantMessage,
				completeAssistantMessage,
				markCompetencyIntro,
				finalizeMessage,
				setCompetencySuggestions,
				applyCompetencyEdit,
				setReportConfig,
				setStatus,
				setStreamStage,
				setStreamMessage,
				setError,
				setGoalSuggestions,
				setGoalImprovements,
				setFormSuggestions,
				setFormQuestionSuggestions,
				setFormImprovements,
				activeInterviewerHarness,
				setActiveInterviewerHarness,
				deactivateInterviewerHarness,
			} = getStoreActions();

			// A caller-supplied harness owns the turn even when no interview is active.
			const effectiveHarness = harness ?? activeInterviewerHarness;
			if (harness && harness !== activeInterviewerHarness) {
				setActiveInterviewerHarness(harness);
			}

			const HARNESS_OUTPUT_HANDLERS: {
				[HARNESS_TYPE in AskSSEInterviewerOutputEvent["harness"]]: (
					event: Extract<AskSSEInterviewerOutputEvent, { harness: HARNESS_TYPE }>,
					msgId: string
				) => void;
			} = {
				competency: (event, msgId) => setCompetencySuggestions(msgId, event.suggestions),
				reporting_analytics_report: (event, msgId) => {
					setReportConfig(msgId, event.reportConfig);
					// Submit completes the interview — clear the active harness so the next user
					// message routes through the classifier instead of re-entering the interviewer.
					deactivateInterviewerHarness();
				},
			};

			// Abort any existing request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			// Create new abort controller
			abortControllerRef.current = new AbortController();

			// Add user message to store
			if (!omitUserMessage) {
				addUserMessage(query);
			}
			// Clear any previous error/stream state and set thinking status
			setError(null);
			setStreamStage(null);
			setStreamMessage(null);
			setStatus("thinking");

			// Push the empty streaming assistant message NOW (not after fetch returns)
			// so the in-message "Thinking…" bubble owns the placeholder from the
			// very first frame. Without this, a separately-styled placeholder would
			// render until startAssistantMessage runs post-fetch, causing a visible
			// size/style flash when the real message bubble takes over.
			const assistantMessageId: string = startAssistantMessage();

			// Idle timeout re-armed on each received event so a long but active stream never hard-fails.
			let suppressHarnessEntryOutput = false;
			const armIdle = () => {
				clearTimeoutRef();
				timeoutRef.current = setTimeout(() => {
					if (abortControllerRef.current) {
						abortControllerRef.current.abort();
						const actions = getStoreActions();
						actions.completeAssistantMessage(assistantMessageId);
						actions.setError("Request timed out. Please try again.");
					}
				}, STREAM_TIMEOUT_MS);
			};
			armIdle();

			try {
				const url = getUrl("/v1/ai/ask/stream");
				const headers = getHeaders({ body: JSON.stringify({}) });
				const interviewNotes = effectiveHarness
					? (() => {
							try {
								const raw = sessionStorage.getItem(getInterviewerNotesKey(effectiveHarness));
								return raw ? JSON.parse(raw) : null;
							} catch {
								return null;
							}
						})()
					: null;

				// Pending suggestions from earlier turns — re-sent so the agent can
				// iterate without losing question-level detail.
				const priorFormSuggestions = useAskPerformYardStore
					.getState()
					.formSuggestionStates.filter((fs) => fs.status === "pending")
					.map((fs) => fs.suggestion);

				const competencyFramework = useAskPerformYardStore.getState().competencyFramework;
				const reviewStatusOf = (id: string) => {
					const itemStatus = competencyFramework?.itemStatuses[id];
					return itemStatus === "created" || itemStatus === "removed" ? itemStatus : "pending";
				};
				const priorCompetencySuggestions = competencyFramework
					? competencyFramework.suggestions.map((suggestion) => ({
							...suggestion,
							...competencyFramework.editedFields[suggestion.id], // fold in unsaved edits
							reviewStatus: reviewStatusOf(suggestion.id),
						}))
					: [];
				// The rule belongs to the competency harness only; a classified turn can still enter it.
				const competencyImportBlocked =
					isCompetencyReimportBlocked(competencyFramework) &&
					(!effectiveHarness || effectiveHarness === "competency");
				// Send the in-progress form so the agent can tailor question suggestions.
				const isOnFormBuilderPage = useAskPerformYardStore.getState().isOnFormBuilderPage;
				let currentForm: FormBuilderContextSnapshot | undefined;
				if (isOnFormBuilderPage) {
					const { name, questions, editingQuestion, activeQuestionValuesReader } =
						useEditFormStore.getState();
					// An open edit card's values live in its form until saved — read them so the
					// snapshot reflects what's on screen, not the last-saved version.
					const liveValues = editingQuestion?.refId ? activeQuestionValuesReader?.() : null;
					currentForm = {
						// Sliced to the contract's payload caps — the builder has no question-count
						// limit, an open card can hold not-yet-validated overlong text, and an
						// oversized snapshot must degrade, not fail validation.
						name: name.slice(0, MAX_CURRENT_FORM_NAME_LENGTH),
						questions: questions.slice(0, MAX_CURRENT_FORM_QUESTIONS).map((storeQuestion) => {
							const q =
								liveValues && storeQuestion.refId === editingQuestion?.refId
									? { ...storeQuestion, ...liveValues }
									: storeQuestion;
							// choices is a union (string[] | RatingScaleOption[]): rating-scale questions keep the
							// structured options (the coach can suggest label/description rewrites); everything
							// else flattens to string labels.
							const ratingScale =
								q.type === "rating-scale"
									? q.choices?.flatMap((c) =>
											typeof c === "string"
												? []
												: [
														{
															value: c?.value ?? "",
															name: c?.name ?? "",
															...(c?.description ? { description: c.description } : {}),
														},
													]
										)
									: undefined;
							const choices =
								q.type === "rating-scale"
									? undefined
									: q.choices
											?.map((c) => (typeof c === "string" ? c : (c?.name ?? "")))
											.filter((c) => c.length > 0);
							return {
								id: q.refId,
								type: q.type,
								question: (q.question ?? "").slice(0, MAX_CURRENT_FORM_QUESTION_LENGTH),
								subtext: (q.subtext ?? "").slice(0, MAX_CURRENT_FORM_SUBTEXT_LENGTH),
								...(choices && choices.length > 0 ? { choices } : {}),
								...(ratingScale && ratingScale.length > 0 ? { rating_scale: ratingScale } : {}),
								...(q.is_required !== undefined ? { is_required: q.is_required } : {}),
							};
						}),
					};
				}

				// Forwarded whenever the chat is open on the goal page so classified turns also carry the goal.
				const { isOnGoalCreationPage, currentGoalSnapshot } = useAskPerformYardStore.getState();
				const currentGoal = isOnGoalCreationPage ? (currentGoalSnapshot ?? undefined) : undefined;

				const contextPayload: Record<string, unknown> = {};
				if (priorFormSuggestions.length > 0) contextPayload.priorFormSuggestions = priorFormSuggestions;
				if (currentForm) contextPayload.currentForm = currentForm;
				if (currentGoal) contextPayload.currentGoal = currentGoal;
				if (competencyImportBlocked && !effectiveHarness) contextPayload.competencyImportBlocked = true;

				const response = await fetch(url, {
					method: "POST",
					headers: {
						...headers,
						"Content-Type": "application/json",
					} as Record<string, string>,
					body: JSON.stringify({
						query,
						sessionId,
						messages: messages.map((m) => ({
							role: m.role,
							content: m.content,
						})),
						...(Object.keys(contextPayload).length > 0 ? { context: contextPayload } : {}),
						...(interviewNotes !== null && { interviewNotes }),
						...(priorCompetencySuggestions.length > 0 ? { priorCompetencySuggestions } : {}),
						...(competencyImportBlocked && effectiveHarness ? { competencyImportBlocked: true } : {}),
						...(documentContent ? { documentContent } : {}),
						...(effectiveHarness
							? { agent: "interviewer", harness: effectiveHarness }
							: agent
								? { agent }
								: {}),
					}),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					throw new Error(errorData.message || `Request failed: ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				// Read the SSE stream
				const reader = response.body.getReader();
				readerRef.current = reader;
				const decoder = new TextDecoder();
				let buffer = "";

				let streamDone = false;
				while (!streamDone) {
					const { done, value } = await reader.read();

					if (done) {
						streamDone = true;
						continue;
					}

					// Decode chunk and add to buffer
					buffer += decoder.decode(value, { stream: true });
					armIdle();

					// Extract and process complete SSE messages
					const { messages: sseMessages, remainingBuffer } = extractSSEMessages(buffer);
					buffer = remainingBuffer;

					for (const message of sseMessages) {
						const parsed = parseSSEMessage(message);

						if (!parsed) {
							continue;
						}

						const { eventType, data } = parsed;

						const result = AskSSEEventSchema.safeParse({ ...(data as object), type: eventType });
						if (!result.success) {
							console.warn("[AskStream] Failed to parse SSE event", { eventType, error: result.error });
							continue;
						}
						const event = result.data;

						switch (event.type) {
							case "status": {
								setStreamStage(event.stage);
								setStreamMessage(event.message ?? null);
								break;
							}
							case "chunk": {
								if (suppressHarnessEntryOutput) break;

								if (useAskPerformYardStore.getState().status === "thinking") {
									setStatus("streaming");
								}
								appendToAssistantMessage(assistantMessageId, event.text);
								break;
							}
							case "goals": {
								setGoalSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "goalImprovements": {
								setGoalImprovements(assistantMessageId, event.suggestions);
								break;
							}
							case "interview_notes": {
								try {
									sessionStorage.setItem(
										getInterviewerNotesKey(event.harness),
										JSON.stringify(event.notes)
									);
								} catch {
									// sessionStorage write failures are non-fatal
								}
								const enteringCompetencyHarness =
									event.harness === "competency" && effectiveHarness !== "competency";
								setActiveInterviewerHarness(event.harness);
								// A blocked entry streams normally — the agent answers, and the prompt rule carries the fact.
								if (
									enteringCompetencyHarness &&
									!isCompetencyReimportBlocked(useAskPerformYardStore.getState().competencyFramework)
								) {
									markCompetencyIntro(assistantMessageId);
									suppressHarnessEntryOutput = true;
									finalizeMessage(assistantMessageId);
								}
								break;
							}
							case "interviewer_output": {
								(
									HARNESS_OUTPUT_HANDLERS[event.harness] as (
										event: AskSSEInterviewerOutputEvent,
										msgId: string
									) => void
								)(event, assistantMessageId);
								setStreamMessage(null);
								break;
							}
							case "exit_interviewer": {
								deactivateInterviewerHarness();
								break;
							}
							case "competency_edit": {
								applyCompetencyEdit(
									{ add: event.add, update: event.update, remove: event.remove },
									assistantMessageId
								);
								break;
							}
							case "forms": {
								setFormSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "formQuestions": {
								setFormQuestionSuggestions(assistantMessageId, event.suggestions);
								break;
							}
							case "formImprovements": {
								setFormImprovements(assistantMessageId, event.suggestions);
								break;
							}
							case "done": {
								completeAssistantMessage(assistantMessageId, event.sources, event.confidence);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
							case "error": {
								// Finalize the in-flight message so it stops rendering the streaming
								// cursor; the error is surfaced separately via the error banner.
								completeAssistantMessage(assistantMessageId);
								setError(event.message);
								clearTimeoutRef();
								reader.cancel();
								return;
							}
						}
					}
				}

				// Stream ended without a done event — the backend likely crashed
				// mid-stream. Complete the message but mark it as incomplete so
				// the UI can show a subtle indicator to the user.
				console.warn("[AskStream] Stream ended without done event");
				clearTimeoutRef();
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);
			} catch (err) {
				// Abort isn't an error, but finalize a still-streaming message so its payload isn't stranded.
				if (err instanceof Error && err.name === "AbortError") {
					const messages = getStoreActions().messages;
					const isLast = messages[messages.length - 1]?.id === assistantMessageId;
					if (messages.find((message) => message.id === assistantMessageId)?.isStreaming) {
						completeAssistantMessage(assistantMessageId, undefined, undefined, true);
					} else if (isLast) {
						// Harness-finalized message: completeAssistantMessage never runs to reset the
						// turn. Guarded on isLast so a superseding stream's status isn't clobbered.
						setStatus("idle");
						setStreamStage(null);
						setStreamMessage(null);
					}
					return;
				}

				// If an error occurred while streaming (e.g. lost connection),
				// complete the in-progress message as incomplete so it doesn't
				// show the streaming cursor forever.
				completeAssistantMessage(assistantMessageId, undefined, undefined, true);

				const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
				getStoreActions().setError(errorMessage);
			} finally {
				abortControllerRef.current = null;
				clearTimeoutRef();
				readerRef.current?.cancel();
				readerRef.current = null;
			}
		},
		[clearTimeoutRef, getStoreActions]
	);

	return {
		sendQuery,
		abort,
		isStreaming: status === "thinking" || status === "streaming",
	};
};
